import { ConfigStatusDot } from "@/components/StatusIcon";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { AdminService } from "@/gen/omnios/v1/admin_connect.js";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Eye, Pause, Play, Plus, Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/admin/workspaces/")({
	component: AdminWorkspaces,
});

const adminClient = createClient(AdminService, transport);

const STATUS_COLORS: Record<string, string> = {
	active: "hsl(var(--chart-2))",
	suspended: "hsl(var(--destructive))",
	trial: "#6366f1",
	churned: "hsl(var(--muted-foreground))",
};

function AdminWorkspaces() {
	const { t } = useTranslation("admin");
	useBreadcrumbs([
		{ label: t("admin_root"), href: "/admin" },
		{ label: t("workspaces_title") },
	]);
	const queryClient = useQueryClient();
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");
	const [createOpen, setCreateOpen] = useState(false);
	const [name, setName] = useState("");
	const [slug, setSlug] = useState("");
	const [plan, setPlan] = useState("free");

	const STATUS_LABELS: Record<string, string> = {
		active: t("workspaces_status_label_active"),
		suspended: t("workspaces_status_label_suspended"),
		trial: t("workspaces_status_label_trial"),
		churned: t("workspaces_status_label_churned"),
	};

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "workspaces", search, statusFilter],
		queryFn: () =>
			adminClient.listAllWorkspaces({
				search,
				statusFilter: statusFilter === "all" ? "" : statusFilter,
				pageSize: 50,
			}),
	});

	const createMutation = useMutation({
		mutationFn: () => adminClient.createWorkspace({ name, slug, plan }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["admin", "workspaces"],
			});
			setCreateOpen(false);
			setName("");
			setSlug("");
			setPlan("free");
		},
	});

	const suspendMutation = useMutation({
		mutationFn: (workspaceId: string) =>
			adminClient.suspendWorkspace({
				workspaceId,
				reason: "Admin suspended",
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["admin", "workspaces"],
			});
		},
	});

	const reactivateMutation = useMutation({
		mutationFn: (workspaceId: string) =>
			adminClient.reactivateWorkspace({ workspaceId }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["admin", "workspaces"],
			});
		},
	});

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-end">
				<Dialog open={createOpen} onOpenChange={setCreateOpen}>
					<DialogTrigger asChild>
						<Button>
							<Plus className="h-4 w-4 mr-1" />
							{t("workspaces_create")}
						</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>{t("workspaces_create_title")}</DialogTitle>
							<DialogDescription>
								{t("workspaces_create_description")}
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-4 pt-2">
							<div className="space-y-2">
								<Label htmlFor="ws-name">{t("workspaces_create_name")}</Label>
								<Input
									id="ws-name"
									placeholder="Acme Corp"
									value={name}
									onChange={(e) => setName(e.target.value)}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="ws-slug">{t("workspaces_create_slug")}</Label>
								<Input
									id="ws-slug"
									placeholder="acme-corp"
									value={slug}
									onChange={(e) => setSlug(e.target.value)}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="ws-plan">{t("workspaces_create_plan")}</Label>
								<Select value={plan} onValueChange={setPlan}>
									<SelectTrigger id="ws-plan">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="free">Free</SelectItem>
										<SelectItem value="starter">Starter</SelectItem>
										<SelectItem value="pro">Pro</SelectItem>
										<SelectItem value="enterprise">Enterprise</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<Button
								onClick={() => createMutation.mutate()}
								disabled={createMutation.isPending || !name || !slug}
								className="w-full"
							>
								{createMutation.isPending
									? t("workspaces_creating")
									: t("common:create")}
							</Button>
						</div>
					</DialogContent>
				</Dialog>
			</div>

			<div className="flex items-center gap-4">
				<div className="relative flex-1 max-w-sm">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("workspaces_search_placeholder")}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-8"
					/>
				</div>
				<Select value={statusFilter} onValueChange={setStatusFilter}>
					<SelectTrigger className="w-[150px]">
						<SelectValue placeholder={t("workspaces_status_placeholder")} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{t("workspaces_status_all")}</SelectItem>
						<SelectItem value="active">
							{t("workspaces_status_active")}
						</SelectItem>
						<SelectItem value="suspended">
							{t("workspaces_status_suspended")}
						</SelectItem>
						<SelectItem value="trial">
							{t("workspaces_status_trial")}
						</SelectItem>
						<SelectItem value="churned">
							{t("workspaces_status_churned")}
						</SelectItem>
					</SelectContent>
				</Select>
			</div>

			<div className="rounded-lg border overflow-hidden">
				<Table>
					<TableHeader>
						<TableRow className="bg-muted/30">
							<TableHead>{t("workspaces_col_name")}</TableHead>
							<TableHead>{t("workspaces_col_status")}</TableHead>
							<TableHead>{t("workspaces_col_plan")}</TableHead>
							<TableHead className="text-right">
								{t("workspaces_col_products")}
							</TableHead>
							<TableHead className="text-right">
								{t("workspaces_col_orders")}
							</TableHead>
							<TableHead className="text-right">
								{t("workspaces_col_members")}
							</TableHead>
							<TableHead />
						</TableRow>
					</TableHeader>
					<TableBody>
						{isLoading ? (
							Array.from({ length: 5 }).map((_, i) => (
								<TableRow key={i} className="hover:bg-muted/50">
									<TableCell>
										<Skeleton className="h-4 w-32" />
									</TableCell>
									<TableCell>
										<Skeleton className="h-4 w-16" />
									</TableCell>
									<TableCell>
										<Skeleton className="h-4 w-12" />
									</TableCell>
									<TableCell>
										<Skeleton className="h-4 w-8 ml-auto" />
									</TableCell>
									<TableCell>
										<Skeleton className="h-4 w-8 ml-auto" />
									</TableCell>
									<TableCell>
										<Skeleton className="h-4 w-8 ml-auto" />
									</TableCell>
									<TableCell />
								</TableRow>
							))
						) : data?.workspaces.length === 0 ? (
							<TableRow>
								<TableCell
									colSpan={7}
									className="text-center text-muted-foreground"
								>
									{t("workspaces_empty")}
								</TableCell>
							</TableRow>
						) : (
							data?.workspaces.map((ws) => (
								<TableRow key={ws.id} className="hover:bg-muted/50">
									<TableCell>
										<div>
											<div className="font-medium">{ws.name}</div>
											<div className="text-xs text-muted-foreground">
												{ws.slug}
											</div>
										</div>
									</TableCell>
									<TableCell>
										<ConfigStatusDot
											color={STATUS_COLORS[ws.status] ?? "hsl(var(--muted-foreground))"}
											label={STATUS_LABELS[ws.status] ?? ws.status}
										/>
									</TableCell>
									<TableCell>{ws.plan}</TableCell>
									<TableCell className="text-right">
										{ws.productCount.toLocaleString()}
									</TableCell>
									<TableCell className="text-right">
										{ws.orderCount.toLocaleString()}
									</TableCell>
									<TableCell className="text-right">{ws.memberCount}</TableCell>
									<TableCell>
										<div className="flex items-center justify-end gap-1">
											<Button variant="ghost" size="icon" asChild>
												<Link to="/admin/workspaces/$id" params={{ id: ws.id }}>
													<Eye className="h-4 w-4" />
												</Link>
											</Button>
											{ws.status === "suspended" ? (
												<Button
													variant="ghost"
													size="icon"
													onClick={() => reactivateMutation.mutate(ws.id)}
													disabled={reactivateMutation.isPending}
												>
													<Play className="h-4 w-4" />
												</Button>
											) : (
												<Button
													variant="ghost"
													size="icon"
													onClick={() => suspendMutation.mutate(ws.id)}
													disabled={suspendMutation.isPending}
												>
													<Pause className="h-4 w-4" />
												</Button>
											)}
										</div>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</div>

			{data && (
				<div className="text-sm text-muted-foreground">
					{t("workspaces_shown_count", {
						shown: data.workspaces.length,
						total: data.totalCount.toLocaleString(),
					})}
				</div>
			)}
		</div>
	);
}
